/**
 * Which account functions a position can carry — a copy of `GROUP_FUNCTIONS` in the ABCP kernel
 * (`@modeso/types__aumico-abcp-kernel-be`). The backend refuses a function that does not fit the position
 * (`ACCOUNT_BINDING_INVALID`), so the Kontenplan offers exactly these. A position missing here has no
 * function — the normal case, not a gap.
 *
 * Copied, not imported: the kernel is CommonJS with its whole rule engine and the AI's prompt texts, and
 * importing it would ship all of that to the browser for two small tables.
 * `src/tests/kernel-copy.unit.spec.ts` compares both tables with the kernel version pinned in the
 * frontend's package.json, so bumping that pin to different rules fails the frontend tests. The pin
 * follows aumico-be by hand.
 *
 * Only the functions the closing's rule table binds against. The kernel enum holds more values, but
 * those are posting targets of the parked booking proposals and are never offered to a user.
 */
export const POSITION_FUNCTIONS: Readonly<Record<string, readonly string[]>> = {
    CURRENT_ASSETS_LIQUID: ['BANK', 'CASH', 'CASH_IN_TRANSIT'],
    CURRENT_ASSETS_MARKETABLE_SECURITIES: ['SHORT_TERM_SECURITIES'],
    CURRENT_ASSETS_RECEIVABLES: ['TRADE_RECEIVABLES', 'ALLOWANCE_TRADE_RECEIVABLES'],
};

/**
 * The functions that need a proof, with the documents that count as one — the kernel's `FUNCTION_RULES`
 * (`proofRequired`, `proofKinds`), each kind as its `DOCUMENT_TYPES` label. German, the language of the
 * documents. In the rule table's order.
 */
export const FUNCTION_PROOFS: Readonly<Record<string, readonly string[]>> = {
    BANK: ['Kontoauszug', 'Zins- / Kapitalausweis', 'Bankbestätigung'],
    CASH: ['Kassenabschluss'],
    SHORT_TERM_SECURITIES: ['Depotauszug'],
    TRADE_RECEIVABLES: ['OP-Liste'],
    ALLOWANCE_TRADE_RECEIVABLES: ['Delkredere-Berechnung'],
};

/** The functions a position allows — empty when it has none or no position is set. */
export function functionsOfPosition(position: string | null): readonly string[] {
    return position === null ? [] : POSITION_FUNCTIONS[position] ?? [];
}

/** The i18n key of one account function. */
export function functionLabelKey(accountFunction: string): string {
    return `abcp.chartOfAccounts.function.${accountFunction}`;
}
